import Image from "next/image";
import Link from "next/link";
import { Clock, Play } from "lucide-react";
import { moduleHref } from "@/components/modules/moduleHref";
import { isFreeToUnlock } from "@/components/modules/paywall";
import { ImageFrame } from "@/components/ui/ImageFrame";
import { formatDuration, formatPrice } from "@/lib/format";
import { modulePoster } from "@/lib/lecturehub/poster";
import type { Dict, Lang } from "@/lib/i18n";
import type { Module } from "@/lib/lecturehub/types";

/**
 * The guest FEATURED band, in four arrangements — CatalogList's `featuredLayout`
 * (grid | editorial | spotlight | mosaic). "grid" is the plain ribbon (handled
 * upstream by ModuleGrid); the other three live here and share one vocabulary:
 *
 *   editorial — a cinematic 16:9 lead + a compact rail of divided rows (the
 *               media.n24.si lead grid, teardown 2026-08-31).
 *   spotlight — a single full-width cinematic banner; one hero, nothing else.
 *   mosaic    — the same lead + a poster-forward tile grid (overlay titles),
 *               for a visual, poster-rich band rather than a text rail.
 *
 * All three reuse LeadFeature and share OverlayMeta / PlayDisc, so they read as
 * one system and re-brand with the portal theme. A lead without a poster sits
 * on the primary-dark gradient FeaturedArticles uses. Guest-only by
 * construction (the ribbon is guest-gated upstream), so `library` never applies.
 */
export function FeaturedEditorial({
  modules,
  lang,
  t,
  cfHash = null,
}: {
  modules: Module[];
  lang: Lang;
  t: Dict;
  cfHash?: string | null;
}) {
  const [lead, ...rail] = modules;
  if (!lead) return null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
      <LeadFeature m={lead} lang={lang} t={t} cfHash={cfHash} wide={rail.length === 0} />
      {rail.length > 0 ? (
        // A structured, divided list that fills the lead's height: each row is
        // flex-1 so the three share the column evenly (no justify-between gaps),
        // with hairline dividers so it reads as one deliberate unit next to the
        // cinematic lead rather than three floating links.
        <div className="flex flex-col divide-y divide-border/60 lg:col-span-5">
          {rail.map((m) => (
            <RailRow key={m.id} m={m} lang={lang} t={t} cfHash={cfHash} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * SPOTLIGHT — a single full-width cinematic banner. One hero, no rail; the
 * remaining featured modules fall through to the grid below. Cinematic wide
 * ratio (not aspect-video, which is too tall full-bleed).
 */
export function FeaturedSpotlight({
  modules,
  lang,
  t,
  cfHash = null,
}: {
  modules: Module[];
  lang: Lang;
  t: Dict;
  cfHash?: string | null;
}) {
  const [lead, ...rest] = modules;
  if (!lead) return null;
  return (
    <div className="flex flex-col gap-4">
      <LeadFeature m={lead} lang={lang} t={t} cfHash={cfHash} spotlight />
      {/* Any other featured items ride below as a poster strip, so none are lost. */}
      {rest.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {rest.map((m) => (
            <MosaicTile key={m.id} m={m} lang={lang} t={t} cfHash={cfHash} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * MOSAIC — the cinematic lead + a poster-forward tile grid of the rest. Titles
 * overlay the posters (minimal chrome), so the band reads visually rather than
 * as a text rail. An odd trailing tile spans both columns so the grid never
 * leaves a hole.
 */
export function FeaturedMosaic({
  modules,
  lang,
  t,
  cfHash = null,
}: {
  modules: Module[];
  lang: Lang;
  t: Dict;
  cfHash?: string | null;
}) {
  const [lead, ...tiles] = modules;
  if (!lead) return null;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-6">
      <LeadFeature m={lead} lang={lang} t={t} cfHash={cfHash} wide={tiles.length === 0} />
      {tiles.length > 0 ? (
        // auto-rows-fr on desktop makes every tile row equal height, so a
        // spanning odd tile matches its neighbours instead of towering; the
        // grid stretches to the lead's height (self-stretch). See MosaicTile
        // for the paired mobile aspect-video → desktop fill-height switch.
        <div className="grid grid-cols-2 gap-4 self-stretch lg:col-span-5 lg:auto-rows-fr">
          {tiles.map((m, i) => (
            <MosaicTile
              key={m.id}
              m={m}
              lang={lang}
              t={t}
              cfHash={cfHash}
              // odd last tile spans the row so there's never an empty cell
              wide={tiles.length % 2 === 1 && i === tiles.length - 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── shared pieces ─────────────────────────── */

// The no-poster ground (and the ground a poster fades in over) — the same
// primary-dark ramp FeaturedArticles' hero uses.
const POSTER_GROUND =
  "linear-gradient(120deg, color-mix(in srgb, var(--color-primary), black 18%) 0%, color-mix(in srgb, var(--color-primary), black 45%) 100%)";

function ottFacts(m: Module) {
  return { duration: m.ott?.duration ?? null, free: isFreeToUnlock(m.price) };
}

// The metadata row the cards speak too: solid brand chip for the type, glass
// chips for duration and free/price — so a feature never says LESS than a card.
function OverlayMeta({ m, t }: { m: Module; t: Dict }) {
  const { duration, free } = ottFacts(m);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <span className="bg-primary text-primary-foreground rounded-pill px-3 py-1 text-xs font-semibold tracking-wide uppercase">
        {t.detail.typeVideo}
      </span>
      {duration ? (
        <span className="rounded-pill inline-flex items-center gap-1.5 bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
          <Clock className="size-3.5" aria-hidden />
          {formatDuration(duration)}
        </span>
      ) : null}
      <span className="rounded-pill inline-flex items-center bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
        {free ? t.detail.free : formatPrice(m.price ?? 0)}
      </span>
    </div>
  );
}

// The white play disc — the affordance that says "this band fronts video". One
// definition, sized by the caller, so every layout's disc matches.
function PlayDisc({
  disc = "size-12",
  icon = "size-5",
  className = "",
}: {
  disc?: string;
  icon?: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`grid place-items-center rounded-full bg-white/90 ${disc} ${className}`}
    >
      <Play className={`text-primary ml-0.5 fill-current ${icon}`} />
    </span>
  );
}

function LeadFeature({
  m,
  lang,
  t,
  cfHash,
  wide = false,
  spotlight = false,
}: {
  m: Module;
  lang: Lang;
  t: Dict;
  cfHash: string | null;
  /** Lone featured (editorial/mosaic with no rail): let the lead widen. */
  wide?: boolean;
  /** Spotlight layout: full-width cinematic banner instead of a column lead. */
  spotlight?: boolean;
}) {
  const poster = modulePoster(m, cfHash);

  // The #player deep link keeps the play disc's promise: the detail page renders
  // its player at that anchor, so the click lands ON the playing surface.
  // aspect-video for the column lead (posters are 16:9); a wider, height-capped
  // ratio for the full-bleed spotlight so it doesn't tower.
  const shape = spotlight
    ? "block w-full aspect-video sm:aspect-[2/1] lg:aspect-[5/2]"
    : `block aspect-video ${wide ? "lg:col-span-8" : "lg:col-span-7"}`;

  return (
    <Link
      href={`${moduleHref(lang, m)}#player`}
      className={`group/lead relative overflow-hidden rounded-card no-underline ${shape}`}
      style={{ background: POSTER_GROUND }}
    >
      {poster ? (
        // alt="" — the lead's h3 carries the title over this photo.
        <Image
          src={poster}
          alt=""
          fill
          sizes={spotlight ? "100vw" : "(min-width: 1024px) 58vw, 100vw"}
          className="object-cover transition-transform duration-500 ease-out group-hover/lead:scale-[1.05]"
        />
      ) : null}
      {/* Readability scrim — literal black on purpose; every overlay card shares it. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

      <PlayDisc className="absolute right-5 bottom-5 transition-transform duration-300 group-hover/lead:scale-110 lg:right-7 lg:bottom-7" />

      <div className="absolute right-0 bottom-0 left-0 p-6 lg:p-8">
        <OverlayMeta m={m} t={t} />
        <h3
          className={`line-clamp-2 leading-tight font-bold text-white transition-colors group-hover/lead:text-[color-mix(in_srgb,var(--color-primary),white_55%)] ${
            spotlight ? "text-2xl lg:text-4xl" : "text-2xl lg:text-3xl"
          }`}
        >
          {m.name}
        </h3>
        {m.abstract ? (
          <p className="mt-2 line-clamp-2 max-w-xl text-sm text-white/85 lg:text-base">
            {m.abstract}
          </p>
        ) : null}
      </div>
    </Link>
  );
}

function RailRow({
  m,
  lang,
  t,
  cfHash,
}: {
  m: Module;
  lang: Lang;
  t: Dict;
  cfHash: string | null;
}) {
  const poster = modulePoster(m, cfHash);
  const { duration, free } = ottFacts(m);

  return (
    <Link
      href={`${moduleHref(lang, m)}#player`}
      // flex-1 + items-center: the rows share the column height evenly and center
      // their content, so the rail fills the lead's height instead of leaving
      // gaps. first/last padding trims to the dividers' outer edges.
      className="group/row flex flex-1 items-center gap-4 py-4 no-underline first:pt-0 last:pb-0"
    >
      <span className="relative block aspect-video w-40 shrink-0 overflow-hidden rounded-lg">
        {poster ? (
          // alt="" — the row's title text sits beside this thumb in the same link.
          <Image
            src={poster}
            alt=""
            fill
            sizes="160px"
            className="object-cover transition-transform duration-500 ease-out group-hover/row:scale-[1.08]"
          />
        ) : (
          <ImageFrame
            src={null}
            monogram={m.name}
            fallback={t.detail.typeVideo}
            className="h-full"
            style={{ aspectRatio: "auto" }}
          />
        )}
        <PlayDisc
          disc="size-8"
          icon="size-3.5"
          className="absolute right-2 bottom-2 shadow-sm transition-transform duration-300 group-hover/row:scale-110"
        />
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
          <span className="text-primary font-semibold tracking-wide uppercase">
            {t.detail.typeVideo}
          </span>
          {duration ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" aria-hidden />
              {formatDuration(duration)}
            </span>
          ) : null}
          <span>{free ? t.detail.free : formatPrice(m.price ?? 0)}</span>
        </span>
        <span className="text-foreground group-hover/row:text-primary line-clamp-2 leading-snug font-semibold transition-colors">
          {m.name}
        </span>
        {m.abstract ? (
          <span className="text-muted-foreground line-clamp-1 text-xs">{m.abstract}</span>
        ) : null}
      </span>
    </Link>
  );
}

function MosaicTile({
  m,
  lang,
  t,
  cfHash,
  wide = false,
}: {
  m: Module;
  lang: Lang;
  t: Dict;
  cfHash: string | null;
  wide?: boolean;
}) {
  const poster = modulePoster(m, cfHash);
  const { duration, free } = ottFacts(m);

  return (
    <Link
      href={`${moduleHref(lang, m)}#player`}
      // Mobile: intrinsic 16:9. Desktop: fill the equal-height grid row
      // (auto-rows-fr on the parent), so a col-span-2 odd tile is the SAME
      // height as its neighbours instead of a 16:9 that towers over them.
      className={`group/tile relative block aspect-video overflow-hidden rounded-card no-underline lg:aspect-auto lg:h-full ${
        wide ? "col-span-2" : ""
      }`}
      style={{ background: POSTER_GROUND }}
    >
      {poster ? (
        // alt="" — the tile's h4 carries the title over this photo.
        <Image
          src={poster}
          alt=""
          fill
          sizes="(min-width: 1024px) 22vw, 50vw"
          className="object-cover transition-transform duration-500 ease-out group-hover/tile:scale-[1.06]"
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
      <PlayDisc
        disc="size-9"
        icon="size-4"
        className="absolute top-3 right-3 transition-transform duration-300 group-hover/tile:scale-110"
      />
      <div className="absolute inset-x-0 bottom-0 p-4">
        <div className="flex flex-wrap items-center gap-x-2 text-[11px] font-medium text-white/85">
          <span className="font-semibold tracking-wide uppercase">{t.detail.typeVideo}</span>
          {duration ? <span>· {formatDuration(duration)}</span> : null}
          <span>· {free ? t.detail.free : formatPrice(m.price ?? 0)}</span>
        </div>
        <h4 className="mt-1 line-clamp-2 text-sm leading-snug font-semibold text-white lg:text-base">
          {m.name}
        </h4>
      </div>
    </Link>
  );
}
